import React, { useState, useRef, useCallback, useEffect } from "react";
import { authFetchInit, hasAuthSession } from "../utils/authSession";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip,
  Divider,
  Alert,
  LinearProgress,
  ButtonBase,
} from "@mui/material";
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useTheme as useCustomTheme } from "../contexts/ThemeContext";
import type { SSOPrefillData } from "../types";
import { API_ROOT } from "../config/apiConfig";
import { canRunVysorWizard, type WizardFlow } from "../utils/vysorFlows";
import {
  fetchVysorContext,
  type VysorAppCatalogItem,
  type VysorIdpOption,
  type VysorQuickAction,
} from "../utils/vysorCatalog";
import { mapQuickActionsToUi } from "../utils/vysorQuickActions";
import {
  advanceVysorWizard,
  buildWizardListPickerMessage,
  buildWizardOptions,
  isCreateInstanceIntent,
  isWizardChipListField,
  type WizardState,
} from "../utils/vysorWizardEngine";
import {
  runWizardAssistGate,
  isAssistCapableWizardField,
  enterWizardHelpMode,
} from "../utils/vysorWizardAssistGates";
import { processWizardAgentChatResponse } from "../utils/vysorWizardAgentBridge";
import {
  buildVysorWizardContext,
  beginPostSetupAppInstanceChain,
  enrichWizardMetadataField,
  handleVysorGuidedAction,
  processInstanceSubFlowStep,
} from "../utils/vysorWizardUi";
import { fetchUrlContentForVysor } from "../utils/vysorMetadata";
import {
  submitIgaEntitlementWizard,
  submitSetupAppWizard,
  submitSsoConfigWizard,
  wizardSubmitErrorMessage,
  wizardSuccessRoute,
} from "../utils/vysorWizardSubmit";
import {
  assistantPrefillSubmitError,
  canSubmitSsoPrefill,
  submitSsoFromAssistantPrefill,
} from "../utils/vysorAssistantPrefill";
import {
  continueChatFollowUpMessage,
  isContinueChatChip,
  planSsoSubmitChip,
} from "../utils/vysorChatSsoChips";
import { renderVysorBotContent } from "../utils/vysorChatRender";
import {
  classifyVysorMessageScope,
  getVysorOutOfScopeReply,
} from "../utils/vysorMessageScope";
import {
  buildAgentContextHeader,
  looksLikeOpenAiConversation,
} from "../utils/vysorWizardAssistCommon";
import { vysorAiGoalOptionId, vysorAiGoalPrompt } from "../utils/vysorAiMode";

const API = `${API_ROOT}/assistant`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface QuickOption {
  id: string;
  label: string;
  description?: string;
  value?: string;
}

interface ChatMessage {
  id: string;
  role: "user" | "bot";
  content: string;
  timestamp: Date;
  options?: QuickOption[];
  prefill?: SSOPrefillData;
  attachmentName?: string;
}

interface PendingAttachment {
  name: string;
  text: string;
  type: "file" | "url";
}

// ─── Component ────────────────────────────────────────────────────────────────

const TurboChatPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [pendingAttachment, setPendingAttachment] =
    useState<PendingAttachment | null>(null);
  const [urlInputOpen, setUrlInputOpen] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const { user } = useAuth();
  const { colors } = useCustomTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [wizard, setWizard] = useState<WizardState | null>(null);
  const [pendingSsoPrefill, setPendingSsoPrefill] =
    useState<SSOPrefillData | null>(null);
  const [appCatalog, setAppCatalog] = useState<VysorAppCatalogItem[]>([]);
  const [configuredIdps, setConfiguredIdps] = useState<VysorIdpOption[]>([]);
  const [roleQuickActions, setRoleQuickActions] = useState<VysorQuickAction[]>(
    [],
  );
  const [catalogStats, setCatalogStats] = useState<{
    totalApps: number;
    pendingSso: number;
  } | null>(null);
  const [agentReady, setAgentReady] = useState(true);
  const wizardRef = useRef<WizardState | null>(null);

  /** Only the latest bot message with options should expose clickable chips (avoids stale wizard clicks). */
  const activeOptionsMessageId = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === "bot" && m.options?.length) return m.id;
    }
    return null;
  }, [messages]);

  // ── scroll ────────────────────────────────────────────────────────────────

  const scrollBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  // ── initialise with welcome ───────────────────────────────────────────────

  const initChat = useCallback(async () => {
    setMessages([]);
    setConversationId(undefined);
    setPendingAttachment(null);
    setWizard(null);
    setPendingSsoPrefill(null);
    initializedRef.current = true;
    setLoading(true);
    try {
      if (!hasAuthSession()) {
        addBotMessage(
          "I need you to be logged in to help you. Please log in and try again.",
        );
        return;
      }
      const [ctx, qsRes] = await Promise.all([
        fetchVysorContext(),
        fetch(`${API}/quick-start`, authFetchInit()),
      ]);
      setAppCatalog(ctx.applications);
      setConfiguredIdps(ctx.configuredIdps || []);
      setRoleQuickActions(ctx.quickActions);
      setCatalogStats({
        totalApps: ctx.stats.totalApps,
        pendingSso: ctx.stats.pendingSso,
      });
      const data = await qsRes.json();
      if (data.success) {
        const welcomeMsg = String(data.data?.message || "");
        setAgentReady(
          !/not configured|AI brain isn't configured|Configure Agent/i.test(
            welcomeMsg,
          ),
        );
        // Never replace start chips with pending-SSO-only. That hid "Add a new app"
        // and left users typing "application" into a dead LLM turn.
        addBotMessage(data.data.message, data.data.options);
        if (data.data.conversationId || data.conversationId) {
          setConversationId(data.data.conversationId || data.conversationId);
        }
      } else {
        setAgentReady(false);
      }
    } catch {
      setAgentReady(false);
      addBotMessage("I couldn't start VYSOR just now. Refresh and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    initChat();
  }, []);
  React.useEffect(() => {
    scrollBottom();
  }, [messages]);

  // ── helpers ───────────────────────────────────────────────────────────────

  function addBotMessage(
    content: string,
    options?: QuickOption[],
    prefill?: SSOPrefillData,
  ) {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        role: "bot",
        content,
        timestamp: new Date(),
        options,
        prefill,
      },
    ]);
  }

  function addUserMessage(content: string, attachmentName?: string) {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        role: "user",
        content,
        timestamp: new Date(),
        attachmentName,
      },
    ]);
  }

  const wizardContext = () => ({
    ...buildVysorWizardContext(user?.role, configuredIdps),
    listPage: wizard?.listPage ?? wizard?.catalogPage,
    listFilter: wizard?.listFilter ?? wizard?.catalogFilter,
    catalogPage: wizard?.listPage ?? wizard?.catalogPage,
    catalogFilter: wizard?.listFilter ?? wizard?.catalogFilter,
  });

  const refreshCatalog = async () => {
    const ctx = await fetchVysorContext();
    setAppCatalog(ctx.applications);
    setConfiguredIdps(ctx.configuredIdps || []);
    setCatalogStats({
      totalApps: ctx.stats.totalApps,
      pendingSso: ctx.stats.pendingSso,
    });
    return ctx.applications;
  };

  useEffect(() => {
    wizardRef.current = wizard;
  }, [wizard]);

  const toQuickOptions = (
    field: import("../utils/vysorFlows").WizardField,
    collected: Record<string, string> = wizard?.collected || {},
    catalog: VysorAppCatalogItem[] = appCatalog,
    pageState?: { listPage?: number; listFilter?: string },
  ): QuickOption[] =>
    buildWizardOptions(field, catalog, collected, {
      ...wizardContext(),
      listPage: pageState?.listPage ?? wizard?.listPage ?? wizard?.catalogPage,
      listFilter:
        pageState?.listFilter ?? wizard?.listFilter ?? wizard?.catalogFilter,
      catalogPage:
        pageState?.listPage ?? wizard?.listPage ?? wizard?.catalogPage,
      catalogFilter:
        pageState?.listFilter ?? wizard?.listFilter ?? wizard?.catalogFilter,
    }).map((o) => ({
      id: o.id,
      label: o.label,
      description: o.description,
      value: o.value,
    }));

  const applyWizardOptions = (
    field: import("../utils/vysorFlows").WizardField,
    collected: Record<string, string>,
    opts?: { withQuestion?: boolean },
  ) => {
    const options = toQuickOptions(field, collected);
    if (opts?.withQuestion) {
      const msg = isWizardChipListField(field)
        ? buildWizardListPickerMessage(
            field,
            appCatalog,
            collected,
            wizardContext(),
            1,
          )
        : `${field.question}${field.hint ? `\n\n_${field.hint}_` : ""}`;
      addBotMessage(msg, options);
      return;
    }
    setMessages((prev) => {
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].role === "bot") {
          const updated = [...prev];
          updated[i] = { ...updated[i], options };
          return updated;
        }
      }
      return [
        ...prev,
        {
          id: Date.now().toString(),
          role: "bot",
          content: "",
          timestamp: new Date(),
          options,
        },
      ];
    });
  };

  const setLastBotOptions = (
    options: {
      id: string;
      label: string;
      description?: string;
      value?: string;
    }[],
  ) => {
    setMessages((prev) => {
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].role === "bot") {
          const updated = [...prev];
          updated[i] = { ...updated[i], options };
          return updated;
        }
      }
      return prev;
    });
  };

  const startWizard = (flow: WizardFlow) => {
    if (!canRunVysorWizard(user?.role, flow.id)) {
      addBotMessage(
        "Only platform administrators can register new applications. You can still configure SSO or manage entitlements for apps already assigned to you.",
      );
      return;
    }
    void (async () => {
      let catalog = appCatalog;
      if (
        flow.fields.some(
          (f) => f.type === "application" || f.type === "instance",
        )
      ) {
        try {
          catalog = await refreshCatalog();
        } catch {
          /* keep existing catalog */
        }
      }
      const collected: Record<string, string> = {};
      setWizard({ flow, stepIndex: 0, collected, listPage: 1 });
      addBotMessage(flow.intro);
      const first = flow.fields[0];
      const pageCtx = { listPage: 1 as const };
      if (isWizardChipListField(first)) {
        addBotMessage(
          buildWizardListPickerMessage(
            first,
            catalog,
            collected,
            {
              ...buildVysorWizardContext(user?.role, configuredIdps),
              listPage: 1,
            },
            1,
          ),
          toQuickOptions(first, collected, catalog, pageCtx),
        );
      } else {
        const hint = first.hint ? `\n\n_${first.hint}_` : "";
        addBotMessage(
          `${first.question}${hint}`,
          toQuickOptions(first, collected, catalog),
        );
      }
    })();
  };

  const cancelWizard = () => {
    setWizard(null);
    addBotMessage("Cancelled. Pick another quick action when you're ready.");
  };

  const callAssistantApi = async (
    msg: string,
    attachment?: PendingAttachment | null,
    optionId?: string,
    ssoPrefillPayload?: SSOPrefillData | null,
  ): Promise<boolean> => {
    if (
      classifyVysorMessageScope(msg, {
        attachmentText: attachment?.text,
        optionId,
        catalogNames: appCatalog.map((a) => a.name),
        sessionMessages: messages.slice(-12).map((m) => ({
          role: m.role === "user" ? "user" : "assistant",
          content: m.content,
        })),
        midActiveFlow: Boolean(
          wizard || pendingSsoPrefill || (conversationId && messages.length > 1),
        ),
      }) === "blocked"
    ) {
      addBotMessage(getVysorOutOfScopeReply());
      return false;
    }

    let res: Response;
    try {
      res = await fetch(
        `${API}/chat`,
        authFetchInit({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: msg,
            conversationId,
            attachmentText: attachment?.text,
            attachmentName: attachment?.name,
            ...(optionId ? { optionId } : {}),
            ...(ssoPrefillPayload ? { ssoPrefill: ssoPrefillPayload } : {}),
          }),
        }),
      );
    } catch {
      addBotMessage(
        "I can't reach VYSOR right now. Check your connection, then try again — or say **list pending SSO** / pick a quick action.",
      );
      return false;
    }

    let data: any = null;
    try {
      data = await res.json();
    } catch {
      addBotMessage(
        res.ok
          ? "I got an unreadable response from the assistant. Please try again."
          : `Assistant request failed (HTTP ${res.status}). Please try again in a moment.`,
      );
      return false;
    }

    if (!res.ok || data?.success === false) {
      const errText =
        (typeof data?.error === "string" && data.error) ||
        (typeof data?.message === "string" && data.message) ||
        `Request failed (HTTP ${res.status})`;
      addBotMessage(
        `I couldn't complete that: ${errText}. Try **list pending SSO**, name an app, or use a quick action.`,
      );
      return false;
    }

    if (data.conversationId) setConversationId(data.conversationId);
    const d = data.data;
    const agentResult = processWizardAgentChatResponse(
      d,
      wizardRef.current,
      appCatalog,
      wizardContext(),
      {
        setWizard,
        addBotMessage: (text, options) => addBotMessage(text, options),
        showWizardField: (field, collected) =>
          addBotMessage(field.question, toQuickOptions(field, collected)),
      },
      canSubmitSsoPrefill,
    );
    if (agentResult.wizardResumed) {
      if (agentResult.advanceAnswer) {
        await advanceWizard(agentResult.advanceAnswer);
      }
      return true;
    }
    if (agentResult.pendingSsoPrefill) {
      setPendingSsoPrefill(agentResult.pendingSsoPrefill);
    }
    return false;
  };

  const advanceWizard = async (
    rawAnswer: string,
    optionId?: string,
    optionValue?: string,
    attachmentText?: string,
  ) => {
    if (!wizard) return;

    let activeWizard = wizard;

    if (activeWizard.subFlow === "add-instance") {
      const handled = await processInstanceSubFlowStep(
        activeWizard,
        rawAnswer,
        optionId,
        optionValue,
        {
          setWizard,
          addBotMessage: (content) => addBotMessage(content),
          applyWizardOptions,
          onRefreshCatalog: refreshCatalog,
          setBusy: setLoading,
        },
      );
      if (handled) return;
    }

    const currentField = activeWizard.flow.fields[activeWizard.stepIndex];
    let result:
      | import("../utils/vysorWizardEngine").WizardAdvanceResult
      | null = null;

    const gate = await runWizardAssistGate(
      activeWizard,
      rawAnswer,
      optionId,
      optionValue,
      appCatalog,
      wizardContext(),
      {
        setWizard,
        addBotMessage: (text, options) =>
          addBotMessage(
            text,
            options?.map((o) => ({
              id: o.id,
              label: o.label,
              description: o.description,
              value: o.value,
            })),
          ),
        showField: (field, collected) =>
          addBotMessage(field.question, toQuickOptions(field, collected)),
        callAgent: async (contextMessage, refText) => {
          setLoading(true);
          try {
            const merged = [refText, attachmentText]
              .filter(Boolean)
              .join("\n\n")
              .trim();
            return await callAssistantApi(
              contextMessage,
              merged
                ? { name: "reference", text: merged, type: "file" as const }
                : null,
            );
          } finally {
            setLoading(false);
          }
        },
        getWizard: () => wizardRef.current,
        setLastBotOptions,
      },
      attachmentText,
    );
    if (gate.handled) return;
    activeWizard = gate.wizard;
    rawAnswer = gate.rawAnswer;
    optionId = gate.optionId;
    optionValue = gate.optionValue;

    const metadataInput = attachmentText?.trim() || rawAnswer;
    if (
      !result &&
      currentField.key === "metadata_or_notes" &&
      metadataInput.trim() &&
      !optionValue
    ) {
      setLoading(true);
      try {
        const enriched = await enrichWizardMetadataField(
          activeWizard.collected,
          metadataInput,
        );
        if (enriched.error) addBotMessage(enriched.error);
        if (enriched.summary) addBotMessage(enriched.summary);
        if (enriched.abortAdvance) {
          addBotMessage(
            currentField.question,
            toQuickOptions(currentField, activeWizard.collected),
          );
          return;
        }
        activeWizard = { ...activeWizard, collected: enriched.collected };
        setWizard(activeWizard);
        rawAnswer = enriched.collected.metadata_or_notes || metadataInput;
      } finally {
        setLoading(false);
      }
    }

    if (!result) {
      result = advanceVysorWizard(
        activeWizard,
        rawAnswer,
        optionId,
        optionValue,
        appCatalog,
        {
          ...wizardContext(),
          listPage: activeWizard.listPage ?? activeWizard.catalogPage,
          listFilter: activeWizard.listFilter ?? activeWizard.catalogFilter,
          catalogPage: activeWizard.listPage ?? activeWizard.catalogPage,
          catalogFilter: activeWizard.listFilter ?? activeWizard.catalogFilter,
        },
      );
    }
    if (!result) return;
    const wizardResult = result;

    if (wizardResult.kind === "cancelled") {
      cancelWizard();
      return;
    }

    if (wizardResult.kind === "repaint-options" && wizardResult.nextField) {
      const patched: WizardState = {
        ...activeWizard,
        ...(wizardResult.wizardPatch || {}),
        collected: wizardResult.collected || activeWizard.collected,
      };
      setWizard(patched);
      const msg =
        wizardResult.pageMessage ||
        (patched.awaitingListFilter || patched.awaitingCatalogFilter
          ? "Type part of a name to filter this list."
          : wizardResult.nextField.question);
      addBotMessage(
        msg,
        toQuickOptions(wizardResult.nextField, patched.collected, appCatalog, {
          listPage: patched.listPage ?? patched.catalogPage,
          listFilter: patched.listFilter ?? patched.catalogFilter,
        }),
      );
      return;
    }

    if (wizardResult.kind === "guided-action" && wizardResult.action) {
      await handleVysorGuidedAction(
        wizardResult.action,
        {
          onRefreshCatalog: refreshCatalog,
          onStartWizard: startWizard,
          onAiGoal: (flowId) => {
            void startAiGoal(
              flowId,
              flowId === "setup-app" ? "Add a new app" : flowId,
            );
          },
          onNavigate: (path) => navigate(path),
          addBotMessage: (content) => addBotMessage(content),
          setWizard,
          getWizard: () => wizardRef.current,
          applyWizardOptions,
        },
        wizardResult.collected,
      );
      return;
    }

    if (wizardResult.kind === "invalid") {
      const current = activeWizard.flow.fields[activeWizard.stepIndex];
      if (isAssistCapableWizardField(activeWizard) && rawAnswer.trim()) {
        const gateRetry = await runWizardAssistGate(
          enterWizardHelpMode(activeWizard),
          rawAnswer,
          optionId,
          optionValue,
          appCatalog,
          wizardContext(),
          {
            setWizard,
            addBotMessage: (text, options) =>
              addBotMessage(
                text,
                options?.map((o) => ({
                  id: o.id,
                  label: o.label,
                  description: o.description,
                  value: o.value,
                })),
              ),
            showField: (field, collected) =>
              addBotMessage(field.question, toQuickOptions(field, collected)),
            callAgent: async (contextMessage, refText) => {
              setLoading(true);
              try {
                const merged = [refText, attachmentText]
                  .filter(Boolean)
                  .join("\n\n")
                  .trim();
                return await callAssistantApi(
                  contextMessage,
                  merged
                    ? { name: "reference", text: merged, type: "file" as const }
                    : null,
                );
              } finally {
                setLoading(false);
              }
            },
            getWizard: () => wizardRef.current,
            setLastBotOptions,
          },
          attachmentText,
        );
        if (gateRetry.handled) return;
      }
      if (rawAnswer.trim() && !optionId && !isCreateInstanceIntent(rawAnswer)) {
        const contextMessage = buildAgentContextHeader(
          `Configure — ${activeWizard.flow.title}`,
          [
            `Current step: ${current.key} — ${current.question}`,
            `Application: ${activeWizard.collected.application_name || "not picked yet"}`,
            `Environment: ${activeWizard.collected.instance_name || "not picked yet"}`,
            "Answer the user's actual question dynamically. Do not use a fixed template.",
            "Stay on this step — do not restart the wizard.",
            "",
            `User: ${rawAnswer}`,
          ],
        );
        await callAssistantApi(
          contextMessage,
          attachmentText
            ? { name: "reference", text: attachmentText, type: "file" as const }
            : null,
        );
        applyWizardOptions(current, activeWizard.collected, {
          withQuestion: false,
        });
        return;
      }
      const hint =
        wizardResult.invalidHint ||
        current.hint ||
        "Please choose one of the options below.";
      addBotMessage(
        `${hint}\n\n${
          isWizardChipListField(current)
            ? buildWizardListPickerMessage(
                current,
                appCatalog,
                activeWizard.collected,
                wizardContext(),
                activeWizard.listPage ?? activeWizard.catalogPage ?? 1,
                activeWizard.listFilter ?? activeWizard.catalogFilter,
              )
            : current.question
        }`,
        toQuickOptions(current, activeWizard.collected, appCatalog, {
          listPage: activeWizard.listPage ?? activeWizard.catalogPage,
          listFilter: activeWizard.listFilter ?? activeWizard.catalogFilter,
        }),
      );
      return;
    }

    if (wizardResult.kind === "complete-sso-chat" && wizardResult.collected) {
      setWizard(null);
      if (
        wizardResult.ssoPrefill?.applicationId &&
        wizardResult.ssoPrefill.instanceId
      ) {
        setLoading(true);
        try {
          const created = await submitSsoConfigWizard(
            wizardResult.collected,
            user?.email,
          );
          await refreshCatalog();
          addBotMessage(
            `SSO configuration submitted for **${wizardResult.collected.application_name}** (${wizardResult.collected.instance_name}).` +
              (created.status ? ` Status: **${created.status}**.` : ""),
          );
          navigate(wizardSuccessRoute("configure-sso", user?.role));
        } catch (err) {
          addBotMessage(
            `Could not submit SSO configuration: ${wizardSubmitErrorMessage(err, "Please try again.")}`,
          );
        } finally {
          setLoading(false);
        }
        return;
      }
      addBotMessage(
        "I need both an application and environment from your catalog before I can submit SSO. Start **Configure SSO** again.",
      );
      return;
    }

    if (
      wizardResult.kind === "complete-navigate" &&
      wizardResult.collected &&
      wizardResult.storagePayload
    ) {
      const flow = activeWizard.flow;
      const storagePayload = wizardResult.storagePayload;
      setWizard(null);

      setLoading(true);
      try {
        if (flow.id === "setup-app") {
          const app = await submitSetupAppWizard(storagePayload, user?.id);
          await refreshCatalog();
          beginPostSetupAppInstanceChain(
            { id: app.id, name: app.name },
            {
              addBotMessage: (text) => addBotMessage(text),
              setWizard,
              applyWizardOptions,
            },
          );
        } else if (flow.id === "iga-entitlements") {
          const appRisk = appCatalog.find(
            (a) => a.id === storagePayload.application_id,
          )?.risk_level;
          const ent = await submitIgaEntitlementWizard(storagePayload, appRisk);
          await refreshCatalog();
          addBotMessage(
            `Entitlement **${ent?.name || storagePayload.name}** was created.`,
          );
          navigate(wizardSuccessRoute("iga-entitlements", user?.role));
        } else {
          addBotMessage(
            "This wizard should complete inside VYSOR. Please try the quick action again.",
          );
        }
      } catch (err) {
        addBotMessage(
          `Could not complete ${flow.title}: ${wizardSubmitErrorMessage(err, "Please try again.")}`,
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    if (
      wizardResult.kind === "next" &&
      wizardResult.nextField &&
      wizardResult.nextStepIndex !== undefined &&
      wizardResult.collected
    ) {
      setWizard({
        flow: activeWizard.flow,
        stepIndex: wizardResult.nextStepIndex,
        collected: wizardResult.collected,
        listPage: 1,
      });
      const nextField = wizardResult.nextField;
      const msg = isWizardChipListField(nextField)
        ? buildWizardListPickerMessage(
            nextField,
            appCatalog,
            wizardResult.collected,
            {
              ...wizardContext(),
              listPage: 1,
              listFilter: undefined,
            },
            1,
          )
        : `${nextField.question}${nextField.hint ? `\n\n_${nextField.hint}_` : ""}`;
      addBotMessage(
        msg,
        toQuickOptions(nextField, wizardResult.collected, appCatalog, {
          listPage: 1,
        }),
      );
    }
  };

  const startAiGoal = async (flowId: WizardFlow["id"], userLabel: string) => {
    if (!canRunVysorWizard(user?.role, flowId)) {
      addUserMessage(userLabel);
      if (flowId === "setup-app") {
        addBotMessage(
          "You need an admin role to register a new application. Ask an admin, or choose **Configure SSO** if the app already exists.",
        );
      } else if (flowId === "configure-sso") {
        addBotMessage(
          "Your role can’t configure SSO in VYSOR. Ask an SSO admin or app owner, or pick another action you can access.",
        );
      } else {
        addBotMessage(
          "Your role can’t decide who can access apps in VYSOR. Ask an IGA admin or app owner, or pick another action you can access.",
        );
      }
      return;
    }
    setWizard(null);
    addUserMessage(userLabel);
    setLoading(true);
    try {
      await callAssistantApi(vysorAiGoalPrompt(flowId), null, vysorAiGoalOptionId(flowId));
    } catch (err: unknown) {
      const detail = err instanceof Error && err.message ? err.message : "";
      addBotMessage(
        detail
          ? `I ran into an error: ${detail}. Try again in a moment, or describe what you need in your own words.`
          : "I ran into an error. Describe what you need in your own words and I’ll help.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOptionClick = async (
    opt: QuickOption | string,
    prefill?: SSOPrefillData,
  ) => {
    const label = typeof opt === "string" ? opt : opt.label;
    const optionId = typeof opt === "object" ? opt.id : undefined;
    const optionValue = typeof opt === "object" ? opt.value : undefined;

    // Prefer structural ids — never let label heuristics steal continue_sso / continue_chat
    if (optionId === "continue_sso") {
      await sendMessage(label, optionId);
      return;
    }

    // AI Mode: goals go to the assistant — not a rigid local wizard loop
    if (
      optionId === "start_setup_app" ||
      ((!optionId || optionId === "start_setup_app") &&
        (/^1\s*[—-]\s*Add a new app$/i.test(label) || /^Add a new app$/i.test(label)))
    ) {
      if (!canRunVysorWizard(user?.role, "setup-app")) {
        addUserMessage(label);
        addBotMessage(
          "You need an admin role to register a new application. Ask an admin, or choose **Configure SSO** if the app already exists.",
        );
        return;
      }
      await startAiGoal("setup-app", label);
      return;
    }
    if (
      optionId === "start_configure_sso" ||
      ((!optionId || optionId === "start_configure_sso") &&
        (/^2\s*[—-]\s*Configure SSO$/i.test(label) ||
          /^(?:Configure|Set up) (?:SSO|company login)$/i.test(label) ||
          /^Configure SSO$/i.test(label) ||
          /^App already exists/i.test(label) ||
          /^Walk me through Configure SSO$/i.test(label)))
    ) {
      if (!canRunVysorWizard(user?.role, "configure-sso")) {
        addUserMessage(label);
        addBotMessage(
          "SSO isn’t available for your role. Ask an admin or try another action.",
        );
        return;
      }
      await startAiGoal("configure-sso", label);
      return;
    }
    if (
      optionId === "start_iga_entitlements" ||
      ((!optionId || optionId === "start_iga_entitlements") &&
        (/^3\s*[—-]\s*IGA entitlements$/i.test(label) ||
          /^Who can access$/i.test(label) ||
          /^IGA entitlements$/i.test(label)))
    ) {
      if (!canRunVysorWizard(user?.role, "iga-entitlements")) {
        addUserMessage(label);
        addBotMessage(
          "Who-can-access isn’t available for your role. Ask an admin, or try another action.",
        );
        return;
      }
      await startAiGoal("iga-entitlements", label);
      return;
    }

    if (
      optionId === "submit_sso" ||
      optionId === "open_sso_form" ||
      label === "Submit SSO configuration" ||
      label === "Open SSO form"
    ) {
      const ssoPrefill = pendingSsoPrefill || prefill;
      const plan = planSsoSubmitChip({
        optionId,
        label,
        pendingSsoPrefill: ssoPrefill,
        hasConversation: Boolean(conversationId),
      });
      if (plan.mode === "direct_prefill") {
        setPendingSsoPrefill(null);
        addUserMessage(label);
        setLoading(true);
        try {
          const created = await submitSsoFromAssistantPrefill(
            plan.prefill,
            user?.email,
          );
          await refreshCatalog();
          addBotMessage(
            `SSO configuration submitted for **${plan.prefill.applicationName || "your application"}**.` +
              (created.status ? ` Status: **${created.status}**.` : ""),
          );
          navigate(wizardSuccessRoute("configure-sso", user?.role));
        } catch (err) {
          addBotMessage(
            `Could not submit SSO configuration: ${assistantPrefillSubmitError(err)}`,
          );
          setPendingSsoPrefill(plan.prefill);
        } finally {
          setLoading(false);
        }
        return;
      }
      if (plan.mode === "agent") {
        addUserMessage(label);
        setLoading(true);
        try {
          await callAssistantApi(
            label,
            null,
            plan.optionId,
            plan.ssoPrefill || ssoPrefill,
          );
        } catch (err) {
          const detail = err instanceof Error && err.message ? err.message : "";
          addBotMessage(
            detail
              ? `I ran into an error: ${detail}. Try again or use a quick action.`
              : "I ran into an error. Try again, or say **list pending SSO**.",
          );
        } finally {
          setLoading(false);
        }
        return;
      }
      return;
    }
    if (isContinueChatChip(optionId, label)) {
      setPendingSsoPrefill(null);
      addUserMessage(label);
      setLoading(true);
      void callAssistantApi(
        continueChatFollowUpMessage(label),
        null,
        "continue_chat",
      ).finally(() => setLoading(false));
      return;
    }
    if (wizard) {
      addUserMessage(label);
      setLoading(true);
      try {
        await advanceWizard(label, optionId, optionValue);
      } catch (err) {
        const detail = err instanceof Error && err.message ? err.message : "";
        addBotMessage(
          detail
            ? `I ran into an error on that step: ${detail}. Pick an option below or try again.`
            : "I ran into an error on that step. Pick an option or try again.",
        );
      } finally {
        setLoading(false);
      }
      return;
    }
    await sendMessage(label, optionId);
  };

  const quickActions = mapQuickActionsToUi(roleQuickActions, {
    onWizard: (flow) => {
      void startAiGoal(flow.id, flow.title);
    },
    onNavigate: (path) => navigate(path),
    userRole: user?.role,
  });

  // ── send message ──────────────────────────────────────────────────────────

  const sendMessage = async (text: string, optionId?: string) => {
    const msg = text.trim();
    if (!msg) {
      if (wizard) {
        const field = wizard.flow.fields[wizard.stepIndex];
        addBotMessage(
          field.required
            ? "This field is required. Please enter a value or choose **Cancel**."
            : "Enter a value or choose **Skip** / **Cancel**.",
        );
      }
      return;
    }
    if (loading) return;

    const attachment = pendingAttachment;
    setPendingAttachment(null);
    setInput("");
    addUserMessage(msg, attachment?.name);

    if (wizard) {
      setLoading(true);
      try {
        // Any free-text on an assist step → model (not keyword gates like "guide me")
        if (
          !optionId &&
          isAssistCapableWizardField(wizard) &&
          looksLikeOpenAiConversation(msg) &&
          !isCreateInstanceIntent(msg) &&
          wizard.subFlow !== "add-instance"
        ) {
          const field = wizard.flow.fields[wizard.stepIndex];
          const contextMessage = buildAgentContextHeader(
            `Configure — ${wizard.flow.title}`,
            [
              `Current step: ${field.key} — ${field.question}`,
              `Application: ${wizard.collected.application_name || "not picked yet"}`,
              `Environment: ${wizard.collected.instance_name || "not picked yet"}`,
              "Answer the user's actual question dynamically. Do not use a fixed template or always give the same style of reply.",
              "Do not restart the wizard. Stay on this step.",
              "",
              `User: ${msg}`,
            ],
          );
          await callAssistantApi(contextMessage, attachment);
          const w = wizardRef.current;
          if (w) {
            const f = w.flow.fields[w.stepIndex];
            if (f) applyWizardOptions(f, w.collected, { withQuestion: false });
          }
          return;
        }
        await advanceWizard(msg, optionId, undefined, attachment?.text);
      } catch (err: unknown) {
        const detail = err instanceof Error && err.message ? err.message : "";
        addBotMessage(
          detail
            ? `I ran into an error: ${detail}. You can pick a chip, retry, or cancel the wizard.`
            : "I ran into an error on that step. You can pick a chip, retry, or cancel the wizard.",
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);

    try {
      await callAssistantApi(msg, attachment, optionId);
    } catch (err: unknown) {
      const detail = err instanceof Error && err.message ? err.message : "";
      addBotMessage(
        detail
          ? `I ran into an error: ${detail}. Try **list pending SSO** or a quick action.`
          : "I ran into an error. Try **list pending SSO** or a quick action.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ── file upload ───────────────────────────────────────────────────────────

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setUploadingDoc(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(
        `${API}/upload-doc`,
        authFetchInit({
          method: "POST",
          body: form,
        }),
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setPendingAttachment({
        name: data.filename,
        text: data.text,
        type: "file",
      });
    } catch (err: any) {
      addBotMessage(`Could not read that file: ${err.message}`);
    } finally {
      setUploadingDoc(false);
    }
  };

  // ── URL fetch ─────────────────────────────────────────────────────────────

  const handleUrlSubmit = async () => {
    const url = urlInput.trim();
    if (!url) return;
    setUrlInputOpen(false);
    setUrlInput("");
    setUploadingDoc(true);
    try {
      const { text } = await fetchUrlContentForVysor(url);
      setPendingAttachment({ name: url, text, type: "url" });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Could not fetch URL";
      addBotMessage(`${msg}. Try pasting the XML content directly.`);
    } finally {
      setUploadingDoc(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  const bubble = (msg: ChatMessage) => {
    const isUser = msg.role === "user";
    return (
      <Box
        key={msg.id}
        sx={{
          display: "flex",
          justifyContent: isUser ? "flex-end" : "flex-start",
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            maxWidth: isMobile ? "90%" : "72%",
            display: "flex",
            flexDirection: "column",
            alignItems: isUser ? "flex-end" : "flex-start",
            gap: 0.5,
          }}
        >
          {/* Avatar row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexDirection: isUser ? "row-reverse" : "row",
            }}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                bgcolor: isUser ? colors.primary : colors.secondary,
                fontSize: "0.8rem",
              }}
            >
              {isUser ? (
                <PersonIcon sx={{ fontSize: 16 }} />
              ) : (
                <BotIcon sx={{ fontSize: 16 }} />
              )}
            </Avatar>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              {isUser
                ? user
                  ? `${user.first_name} ${user.last_name}`.trim() ||
                    user.username ||
                    "You"
                  : "You"
                : "VYSOR"}
            </Typography>
          </Box>

          {/* Bubble */}
          <Paper
            elevation={0}
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: isUser ? colors.primary : "background.paper",
              color: isUser ? "#fff" : "text.primary",
              borderRadius: isUser
                ? "16px 4px 16px 16px"
                : "4px 16px 16px 16px",
              border: isUser ? "none" : `1px solid ${theme.palette.divider}`,
              fontSize: "0.82rem",
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
            }}
          >
            {isUser ? msg.content : renderVysorBotContent(msg.content)}
            {msg.attachmentName && (
              <Chip
                icon={<AttachFileIcon sx={{ fontSize: 12 }} />}
                label={
                  msg.attachmentName.length > 40
                    ? msg.attachmentName.slice(0, 40) + "…"
                    : msg.attachmentName
                }
                size="small"
                sx={{
                  mt: 0.5,
                  fontSize: "0.7rem",
                  height: 20,
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "inherit",
                }}
              />
            )}
          </Paper>

          {/* Quick-reply options */}
          {msg.options &&
            msg.options.length > 0 &&
            msg.id === activeOptionsMessageId && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  mt: 0.5,
                }}
              >
                {msg.options.map((opt) => (
                  <Chip
                    key={opt.id}
                    label={opt.label}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => void handleOptionClick(opt, msg.prefill)}
                    sx={{ fontSize: "0.75rem", height: 26 }}
                  />
                ))}
              </Box>
            )}

          {/* Prefill card */}
          {msg.prefill && (
            <Alert
              severity={
                msg.prefill.applicationId && msg.prefill.instanceId
                  ? "success"
                  : "warning"
              }
              sx={{ mt: 0.5, fontSize: "0.78rem", width: "100%" }}
            >
              {msg.prefill.applicationId && msg.prefill.instanceId
                ? "SSO is ready — use Submit SSO configuration above (stays in chat)."
                : "Draft started — confirm the app and environment in chat before submitting."}
            </Alert>
          )}

          <Typography
            sx={{ fontSize: "0.68rem", color: "text.disabled", mt: 0.25 }}
          >
            {msg.timestamp.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        </Box>
      </Box>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        position: "fixed",
        top: 64,
        left: "var(--provysn-rail, 0px)",
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        zIndex: 1,
      }}
    >
      {/* Header */}
      <Paper
        elevation={1}
        sx={{
          px: 3,
          py: 1.75,
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BotIcon sx={{ color: "primary.main", fontSize: 22 }} />
          <Box>
            <Typography
              sx={{ fontSize: "1.1rem", fontWeight: 700, lineHeight: 1.2 }}
            >
              VYSOR
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
              Your guide for apps, SSO & who can access them
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={agentReady ? "Ready" : "Limited"}
            color={agentReady ? "success" : "warning"}
            size="small"
            icon={<CheckCircleIcon />}
            sx={{ fontSize: "0.72rem", height: 22 }}
          />
          <Tooltip title="Restart conversation">
            <IconButton size="small" onClick={initChat}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Quick actions — deterministic shortcuts always available at top of chat */}
      <Paper
        elevation={0}
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "text.secondary",
              letterSpacing: 0.4,
              textTransform: "uppercase",
            }}
          >
            Quick actions
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: "text.disabled" }}>
            {catalogStats ? (
              <>
                {`${catalogStats.totalApps} app(s) · `}
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigate("/sso-review?pipeline=pending")}
                  sx={{ minWidth: 0, p: 0, fontSize: "0.72rem", textTransform: "none", verticalAlign: "baseline" }}
                >
                  {`${catalogStats.pendingSso} pending SSO review`}
                </Button>
              </>
            ) : (
              "Tailored to your role"
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(auto-fill, minmax(210px, 1fr))",
            },
            gap: 1.5,
          }}
        >
          {quickActions.map((qa) => (
            <ButtonBase
              key={qa.id}
              onClick={qa.onClick}
              sx={{
                textAlign: "left",
                borderRadius: 2,
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                transition: "all 0.18s ease",
                "&:hover": {
                  borderColor: qa.color,
                  backgroundColor: `${qa.color}0d`,
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${qa.color}1a`,
                  color: qa.color,
                  flexShrink: 0,
                }}
              >
                {qa.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  {qa.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "text.secondary",
                    mt: 0.25,
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {qa.description}
                </Typography>
              </Box>
              <ArrowForwardIcon
                sx={{ color: qa.color, fontSize: 18, flexShrink: 0 }}
              />
            </ButtonBase>
          ))}
        </Box>
      </Paper>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {loading && messages.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 1.5,
            }}
          >
            <BotIcon sx={{ color: "primary.main", fontSize: 36 }} />
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
              VYSOR is getting ready…
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: "text.secondary",
                textAlign: "center",
                maxWidth: 360,
              }}
            >
              Ask anything about adding apps, SSO, or who can access
              them.
            </Typography>
          </Box>
        )}
        {messages.map(bubble)}

        {/* Typing indicator */}
        {loading && messages.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: colors.secondary }}>
              <BotIcon sx={{ fontSize: 16 }} />
            </Avatar>
            <Paper
              elevation={0}
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: "4px 16px 16px 16px",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 0.4,
                  alignItems: "center",
                  height: 16,
                }}
              >
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "text.secondary",
                      animation: "bounce 1.2s ease-in-out infinite",
                      animationDelay: `${i * 0.2}s`,
                      "@keyframes bounce": {
                        "0%,80%,100%": { transform: "scale(0.6)" },
                        "40%": { transform: "scale(1)" },
                      },
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Upload progress */}
      {uploadingDoc && <LinearProgress sx={{ height: 2 }} />}

      {/* URL input bar */}
      {urlInputOpen && (
        <Paper
          elevation={0}
          sx={{
            mx: 2,
            mb: 1,
            p: 1,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            display: "flex",
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            autoFocus
            size="small"
            placeholder="Paste documentation URL…"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleUrlSubmit();
              if (e.key === "Escape") setUrlInputOpen(false);
            }}
            sx={{ "& .MuiInputBase-input": { fontSize: "0.82rem" } }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={handleUrlSubmit}
            disabled={!urlInput.trim()}
          >
            Fetch
          </Button>
          <IconButton size="small" onClick={() => setUrlInputOpen(false)}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Paper>
      )}

      {/* Pending attachment badge */}
      {pendingAttachment && (
        <Box
          sx={{
            mx: 2,
            mb: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Chip
            icon={
              pendingAttachment.type === "url" ? (
                <LinkIcon sx={{ fontSize: 12 }} />
              ) : (
                <AttachFileIcon sx={{ fontSize: 12 }} />
              )
            }
            label={
              pendingAttachment.name.length > 50
                ? pendingAttachment.name.slice(0, 50) + "…"
                : pendingAttachment.name
            }
            size="small"
            color="primary"
            variant="outlined"
            onDelete={() => setPendingAttachment(null)}
            sx={{ fontSize: "0.72rem", height: 22 }}
          />
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
            will be sent with your message
          </Typography>
        </Box>
      )}

      {/* Input area */}
      <Paper
        elevation={2}
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          {/* Attach file */}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".pdf,.docx,.txt,.md,.xml"
            onChange={handleFileSelect}
          />
          <Tooltip title="Upload document (PDF, DOCX, TXT, MD, XML)">
            <span>
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingDoc}
              >
                <AttachFileIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>

          {/* URL link */}
          <Tooltip title="Fetch docs from URL">
            <IconButton
              size="small"
              onClick={() => setUrlInputOpen((v) => !v)}
              disabled={uploadingDoc}
            >
              <LinkIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Ask VYSOR anything — add an app, set up login, or who can access…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(input);
              }
            }}
            disabled={loading}
            size="small"
            sx={{ "& .MuiInputBase-input": { fontSize: "0.82rem" } }}
          />

          <IconButton
            onClick={() => sendMessage(input)}
            disabled={(!input.trim() && !pendingAttachment) || loading}
            sx={{
              bgcolor: colors.primary,
              color: "#fff",
              "&:hover": { bgcolor: colors.dark },
              "&:disabled": { bgcolor: "action.disabledBackground" },
              width: 36,
              height: 36,
            }}
          >
            {loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SendIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>

        <Typography
          sx={{ fontSize: "0.68rem", color: "text.disabled", mt: 0.5 }}
        >
          Attach PDF/DOCX/TXT or paste a URL · Enter to send · Shift+Enter for
          new line
        </Typography>
      </Paper>
    </Box>
  );
};

export default TurboChatPage;
